import {
  useSendEmailMutation,
  useInvoicesPdfMutation,
  useEmailSignatureQuery,
  useCustomerRepresentativeByPaymentIdQuery,
  useCustomerRepresentativeQuery,
  usePaymentComposerContextQuery,
} from "../api/queries";
import { useQueryClient } from "@tanstack/react-query";
import { draftFeedbackQueryKey } from "../api/queries/draftFeedbackQueries";
import { paymentKeys } from "../api/queries/queryKeys";
import apiClient from "../api/apiClient";
import {
  Loader,
  ActionIcon,
  Text,
  TextInput,
  TagsInput,
  Button,
  Menu,
} from "@mantine/core";
import { EmailEditor } from "./EmailEditor";
import { useEmailEditor } from "../hooks/useEmailEditor";
import {
  useState,
  useEffect,
  useRef,
  useMemo,
  type MutableRefObject,
} from "react";
import settings from "../settings";
import {
  IconSend,
  IconRefresh,
  IconFileInvoice,
  IconCalendarClock,
  IconChevronDown,
  IconFileText,
  IconPaperclip,
  IconMail,
  IconMailForward,
} from "@tabler/icons-react";
import {
  EmailPromptBubbles,
  type EmailQuickPrompt,
} from "./EmailPromptBubbles";
import { EmailDraft } from "../utils/interfaces";
import type { Payment } from "../utils/interfaces";
import { AttachmentsPreviewPanel } from "./AttachmentsPreviewPanel";
import { base64ToPdfFile } from "../utils/utilsFunctions/functions";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAutoSaveDraft } from "../hooks/useAutoSaveDraft";
import { SendActionModal } from "./SendActionModal";
import { FollowUpModal } from "./FollowUpModal";
import { getInvoicePdfAttachErrorNotice } from "../utils/invoicePdfErrors";
import {
  SendEmailIntentionStatusModal,
  type LinkedInvoiceStatus,
} from "./SendEmailIntentionStatusModal";
import { ChooseInvoicesModal } from "./ChooseInvoicesModal";
import { InvoiceTemplateDraftGenerator } from "./InvoiceTemplateDraftGenerator";
import { useAuth } from "../hooks/useAuth";
import { parseDraftHtml } from "../utils/parseDraftHtml";
import { parseInvoiceExternalIdsFromDraftUrl } from "../utils/parseInvoiceExternalIdsFromDraftUrl";
import { isAutoAttachSendBlocked } from "./emailComposerSendGuard";

interface EmailComposerProps {
  emailDraft: EmailDraft | null | undefined;
  customerEmail?: string;
  invoiceId?: string;
  latestThreadSubject?: string | null;
  language?: string;
  threadId?: string | null;
  replyMessageId?: string | null;
  initialTo?: string[];
  initialCc?: string[];
  initialSubject?: string;
  initialBodyHtml?: string;
  paymentExternalIds?: string[];
  customerRepId?: number;
  openInvoiceExternalIds?: string[];
  onSendSuccess?: () => void;
  navigateOnSuccess?: boolean;
  enableAiAssist?: boolean;
  quickPrompts?: EmailQuickPrompt[];
  editorMinHeight?: number;
  quotedReplyTailHtml?: string;
  invalidateReceivables?: boolean;
  composerDisabled?: boolean;
  /** Parent modals: call before dismiss; skip on Send success. */
  discardGuardRef?: MutableRefObject<(() => boolean) | null>;
}

const normalizeEmailAddresses = (
  value: string | string[] | null | undefined,
): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const normalizeEmails = (values: string[]) =>
  values.map((value) => value.trim()).filter(Boolean);

const normalizeAndDedupeEmails = (values: string[]) =>
  Array.from(new Set(normalizeEmails(values)));

const composerInputStyles = {
  input: {
    fontSize: "14px",
  },
};

const tagInputStyles = {
  ...composerInputStyles,
  pill: {
    fontSize: "13px",
  },
};

export const EmailComposer = ({
  emailDraft,
  customerEmail,
  invoiceId,
  latestThreadSubject,
  language,
  threadId: threadIdProp,
  replyMessageId: replyMessageIdProp,
  initialTo,
  initialCc,
  initialSubject,
  initialBodyHtml,
  paymentExternalIds,
  customerRepId,
  openInvoiceExternalIds,
  onSendSuccess,
  navigateOnSuccess,
  enableAiAssist,
  quickPrompts,
  editorMinHeight,
  quotedReplyTailHtml,
  invalidateReceivables,
  composerDisabled = false,
  discardGuardRef,
}: EmailComposerProps) => {
  const { canMutateOrg } = useAuth();
  const isExplicitThreadReply = threadIdProp != null;
  const showAiAssist = enableAiAssist ?? Boolean(invoiceId);
  const shouldNavigateOnSuccess = navigateOnSuccess ?? Boolean(invoiceId);

  const [subject, setSubject] = useState(
    initialSubject ?? emailDraft?.subject ?? latestThreadSubject ?? "",
  );
  const hasUserEditedSubject = useRef(false);
  const hasUserEditedBody = useRef(false);
  const quotedReplyTailRef = useRef(quotedReplyTailHtml ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [emailToAddress, setEmailToAddress] = useState<string[]>(() => {
    if (initialTo?.length) return initialTo;
    if (emailDraft?.to) {
      return normalizeEmailAddresses(emailDraft.to);
    }
    return customerEmail ? [customerEmail] : [];
  });
  const [emailCcAddress, setEmailCcAddress] = useState<string[]>(() => {
    if (initialCc?.length) return initialCc;
    return normalizeEmailAddresses(emailDraft?.cc);
  });
  const [emailBccAddress, setEmailBccAddress] = useState<string[]>(() =>
    normalizeEmailAddresses(emailDraft?.bcc),
  );
  const [emailToError, setEmailToError] = useState<string | null>(null);
  const [emailCcError, setEmailCcError] = useState<string | null>(null);
  const [emailBccError, setEmailBccError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [emailDraftModificationPrompt, setEmailDraftModificationPrompt] =
    useState("");
  const [toggleCcBcc, setToggleCcBcc] = useState(() =>
    Boolean(initialCc?.length || emailDraft?.cc?.length),
  );
  const [loadingPromptLabel, setLoadingPromptLabel] = useState<string | null>(
    null,
  );
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [chooseInvoicesModalOpen, setChooseInvoicesModalOpen] = useState(false);
  const [isAddingSelectedInvoices, setIsAddingSelectedInvoices] =
    useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<
    "updateDescription" | null
  >(null);
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
  const [sendAsNewEmail, setSendAsNewEmail] = useState(true);
  const [selectedReplyThreadId, setSelectedReplyThreadId] = useState<
    string | null
  >(null);
  const [initialModalView, setInitialModalView] = useState<
    "pickThread" | "updateDescription" | null
  >(null);
  const [attachedInvoiceExternalIds, setAttachedInvoiceExternalIds] = useState<
    string[]
  >([]);
  const [intentionStatusModalOpen, setIntentionStatusModalOpen] =
    useState(false);
  const [linkedInvoicesForModal, setLinkedInvoicesForModal] = useState<
    LinkedInvoiceStatus[]
  >([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (isExplicitThreadReply) return;
    setSendAsNewEmail(!emailDraft?.thread_id);
  }, [emailDraft?.thread_id, isExplicitThreadReply]);

  useEffect(() => {
    if (isExplicitThreadReply || !emailDraft?.thread_id) return;
    setSelectedReplyThreadId(emailDraft.thread_id);
  }, [emailDraft?.thread_id, isExplicitThreadReply]);

  const sendEmailMutation = useSendEmailMutation();
  const isSentEmailLoading = sendEmailMutation.isPending;

  const { data: customerRepByPayment } =
    useCustomerRepresentativeByPaymentIdQuery(invoiceId, {
      enabled: Boolean(invoiceId),
    });
  const { data: customerRepById } = useCustomerRepresentativeQuery(
    customerRepId,
    { enabled: Boolean(customerRepId) && !invoiceId },
  );
  const customerRep = customerRepByPayment ?? customerRepById;
  const effectiveLanguage = language ?? customerRep?.language;

  const { data: composerContext } = usePaymentComposerContextQuery(invoiceId, {
    enabled: Boolean(invoiceId) && !isExplicitThreadReply,
  });

  const threadOptions = composerContext?.thread_options ?? [];
  const hasThread = threadOptions.length > 0;
  const effectiveReplyThreadId =
    selectedReplyThreadId ??
    emailDraft?.thread_id ??
    threadOptions[0]?.thread_id ??
    null;
  const replyThreadSubject = effectiveReplyThreadId
    ? (threadOptions.find((t) => t.thread_id === effectiveReplyThreadId)
        ?.subject ??
      composerContext?.draft_thread_subject ??
      null)
    : null;

  useEffect(() => {
    if (
      isExplicitThreadReply ||
      sendAsNewEmail ||
      threadOptions.length === 0 ||
      selectedReplyThreadId !== null
    ) {
      return;
    }
    setSelectedReplyThreadId(
      emailDraft?.thread_id ?? threadOptions[0]?.thread_id ?? null,
    );
  }, [
    isExplicitThreadReply,
    sendAsNewEmail,
    threadOptions.length,
    emailDraft?.thread_id,
    selectedReplyThreadId,
  ]);

  const resolvedThreadId = useMemo(() => {
    if (threadIdProp != null) return threadIdProp;
    if (invoiceId && hasThread && !sendAsNewEmail) {
      return effectiveReplyThreadId;
    }
    if (sendAsNewEmail) return null;
    return emailDraft?.thread_id ?? null;
  }, [
    threadIdProp,
    invoiceId,
    hasThread,
    sendAsNewEmail,
    effectiveReplyThreadId,
    emailDraft?.thread_id,
  ]);

  const resolvedReplyMessageId = useMemo(() => {
    if (replyMessageIdProp != null) return replyMessageIdProp;
    if (sendAsNewEmail && threadIdProp == null) return null;
    return emailDraft?.reply_message_id ?? null;
  }, [
    replyMessageIdProp,
    sendAsNewEmail,
    threadIdProp,
    emailDraft?.reply_message_id,
  ]);

  const effectiveReplyMode =
    !isExplicitThreadReply && hasThread && !sendAsNewEmail;
  const threadSubjectDisplay = replyThreadSubject ?? "this thread";
  const truncatedThreadSubject =
    threadSubjectDisplay.length > 60
      ? `${threadSubjectDisplay.slice(0, 57)}...`
      : threadSubjectDisplay;
  const threadModeLabel = effectiveReplyMode
    ? `Reply in thread: ${truncatedThreadSubject}`
    : "New email";

  const [isStreaming, setIsStreaming] = useState(false);
  const isRegenerating = isStreaming;
  const eventSourceRef = useRef<EventSource | null>(null);
  const emailEditor = useEmailEditor(
    parseDraftHtml(initialBodyHtml || emailDraft?.text || ""),
    effectiveLanguage,
  );

  // Get current email content for auto-save
  const currentEmailContent = emailEditor.getHTML();

  // Auto-save draft hook (must be after emailEditor is declared)
  const autoSaveStatus = useAutoSaveDraft({
    invoiceId: invoiceId,
    draftUid: emailDraft?.uid,
    emailContent: currentEmailContent,
    subject: subject,
    to: emailToAddress,
    cc: emailCcAddress,
    bcc: emailBccAddress,
    threadId: resolvedThreadId,
    replyMessageId: resolvedReplyMessageId,
  });

  const handleActionAndSend = async () => {
    await requestSend();
  };
  const { mutateAsync: fetchInvoicesPdf } = useInvoicesPdfMutation();
  const { data: signature } = useEmailSignatureQuery();
  const [pdfUrls, setPdfUrls] = useState<Map<string, string>>(new Map());
  const queryClient = useQueryClient();

  const addInvoicePdfsToAttachments = async (invoiceIds: string[]) => {
    if (invoiceIds.length === 0) return;

    const batchResponse = await fetchInvoicesPdf(invoiceIds);
    const filesToAdd: File[] = [];
    const succeededExternalIds: string[] = [];
    const errors: Array<{ externalId: string; error?: string }> = [];
    batchResponse.results.forEach((result) => {
      if (result.status === "success" && result.pdf_base64) {
        try {
          const file = base64ToPdfFile(result.external_id, result.pdf_base64);
          filesToAdd.push(file);
          succeededExternalIds.push(result.external_id);
        } catch (conversionError) {
          errors.push({
            externalId: result.external_id,
            error:
              conversionError instanceof Error
                ? conversionError.message
                : String(conversionError),
          });
        }
      } else {
        const errorMessage =
          result.error ?? "Unknown error fetching invoice PDF";
        errors.push({ externalId: result.external_id, error: errorMessage });
      }
    });
    if (filesToAdd.length > 0) {
      setAttachments((prev) => {
        const existingNames = new Set(prev.map((file) => file.name));
        const uniqueNewFiles = filesToAdd.filter(
          (file) => !existingNames.has(file.name),
        );
        return [...prev, ...uniqueNewFiles];
      });
      setAttachedInvoiceExternalIds((prev) => {
        const next = new Set(prev);
        succeededExternalIds.forEach((id) => next.add(id));
        return Array.from(next);
      });
    }
    const errorNotice = getInvoicePdfAttachErrorNotice(errors);
    if (errorNotice) {
      console.error("Failed to fetch some invoice PDFs:", errors);
      toast.error(errorNotice.message);
    }
  };

  const invoiceIdsForAttach = useMemo((): string[] => {
    if (openInvoiceExternalIds && openInvoiceExternalIds.length > 0) {
      return openInvoiceExternalIds;
    }
    const draftUrl = emailDraft?.uid;
    const invoiceIdsFromDraftUrl = draftUrl
      ? parseInvoiceExternalIdsFromDraftUrl(draftUrl)
      : [];
    if (invoiceIdsFromDraftUrl.length > 1) {
      return invoiceIdsFromDraftUrl;
    }
    if (invoiceId) {
      return [invoiceId];
    }
    if (invoiceIdsFromDraftUrl.length === 1) {
      return invoiceIdsFromDraftUrl;
    }
    if (paymentExternalIds && paymentExternalIds.length > 0) {
      return paymentExternalIds;
    }
    return [];
  }, [openInvoiceExternalIds, emailDraft?.uid, invoiceId, paymentExternalIds]);

  const invoiceIdsForAttachKey = invoiceIdsForAttach.join(",");
  const autoAttachAttemptedForUidRef = useRef<string | null>(null);
  const isAutoAttachingInvoicePdfs = isAutoAttachSendBlocked(
    emailDraft,
    isLoadingInvoices,
  );

  const blockSendIfAutoAttaching = () => {
    if (!isAutoAttachingInvoicePdfs) return false;
    toast.info("Attaching invoice PDFs. Please wait before sending.");
    return true;
  };

  useEffect(() => {
    const draftUid = emailDraft?.uid ?? null;
    const shouldAttach = Boolean(emailDraft?.attach_invoice_pdfs);
    if (!shouldAttach || !draftUid || invoiceIdsForAttach.length === 0) {
      return;
    }
    if (autoAttachAttemptedForUidRef.current === draftUid) {
      return;
    }
    autoAttachAttemptedForUidRef.current = draftUid;

    let cancelled = false;
    (async () => {
      setIsLoadingInvoices(true);
      try {
        await addInvoicePdfsToAttachments(invoiceIdsForAttach);
      } catch (error) {
        if (!cancelled) {
          console.error("Auto-attach invoice PDFs failed", error);
          toast.error(
            "Could not attach some invoice PDFs. Use Attach to retry.",
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingInvoices(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    emailDraft?.uid,
    emailDraft?.attach_invoice_pdfs,
    invoiceIdsForAttachKey,
  ]);

  useEffect(() => {
    // Revoke old blob URLs
    pdfUrls.forEach((url) => {
      if (url.startsWith("blob:")) {
        URL.revokeObjectURL(url);
      }
    });

    const urls = new Map<string, string>();

    // Process files asynchronously to create data URIs
    const processFiles = async () => {
      for (let index = 0; index < attachments.length; index++) {
        const file = attachments[index];
        if (
          file.type === "application/pdf" ||
          file.name.toLowerCase().endsWith(".pdf")
        ) {
          try {
            // Convert File to data URI for embedded preview (works better than blob URLs in iframes)
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });

            urls.set(`${index}`, dataUrl);
          } catch (error) {
            // Fallback to blob URL if data URI creation fails
            const url = URL.createObjectURL(file);
            urls.set(`${index}`, url);
          }
        }
      }

      setPdfUrls(urls);
    };

    processFiles();

    return () => {
      // Cleanup: revoke blob URLs only (data URIs don't need cleanup)
      urls.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [attachments]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    // Update subject if we don't have a draft subject but have a latest thread subject
    if (
      !hasUserEditedSubject.current &&
      !emailDraft?.subject &&
      latestThreadSubject &&
      !subject
    ) {
      setSubject(latestThreadSubject);
    }
  }, [emailDraft?.subject, latestThreadSubject, subject]);

  useEffect(() => {
    if (!emailEditor || hasUserEditedBody.current) return;
    const source = initialBodyHtml || emailDraft?.text;
    if (!source) return;
    emailEditor.commands.setContent(parseDraftHtml(source));
  }, [emailDraft?.text, emailEditor, initialBodyHtml]);

  useEffect(() => {
    if (!emailEditor) return;
    const markEdited = () => {
      hasUserEditedBody.current = true;
    };
    emailEditor.on("update", markEdited);
    return () => {
      emailEditor.off("update", markEdited);
    };
  }, [emailEditor]);

  const handleSubjectChange = (value: string) => {
    hasUserEditedSubject.current = true;
    setSubject(value);
  };

  const isComposerDirty = () =>
    hasUserEditedSubject.current || hasUserEditedBody.current;

  const confirmDiscardIfDirty = () => {
    if (!isComposerDirty()) return true;
    return window.confirm(
      "You have unsaved email changes. Discard them?",
    );
  };

  useEffect(() => {
    if (!discardGuardRef) return;
    discardGuardRef.current = confirmDiscardIfDirty;
    return () => {
      discardGuardRef.current = null;
    };
  }, [discardGuardRef]);

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isComposerDirty()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  const getEmailBodyHtml = () => {
    const composeHtml = emailEditor.getHTML();
    const quotedTail = quotedReplyTailRef.current;
    if (!quotedTail) return composeHtml;
    return `${composeHtml}${quotedTail}`;
  };

  const startStream = async (prompt: string, onDone?: () => void) => {
    const previousEmailText = emailEditor.getHTML();
    const token = localStorage.getItem("token");
    const uid = localStorage.getItem("uid");
    const params = new URLSearchParams({
      email_text: previousEmailText,
      user_prompt: prompt,
    });
    if (token) params.append("token", token);
    if (uid) params.append("uid", uid);
    if (invoiceId) params.append("payment_external_id", invoiceId);
    for (const paymentExternalId of invoiceIdsForAttach) {
      params.append("payment_external_ids", paymentExternalId);
    }
    if (resolvedThreadId) params.append("thread_id", resolvedThreadId);
    if (signature?.enabled) params.append("signature_enabled", "true");
    const base =
      (settings.baseUrl && settings.baseUrl.trim().replace(/\/$/, "")) ||
      "/api";
    const url = `${base}/payments/email/regenerate_stream?${params.toString()}`;

    // Close any previous stream
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    setIsStreaming(true);
    let buffer = "";
    let receivedContent = false;
    const controller = new AbortController();
    const esLike = { close: () => controller.abort() } as EventSource;
    eventSourceRef.current = esLike;
    let finished = false;

    const finishStream = (didSucceed: boolean) => {
      if (finished) return;
      finished = true;
      controller.abort();
      eventSourceRef.current = null;
      setIsStreaming(false);
      setLoadingPromptLabel(null);
      setEmailDraftModificationPrompt("");
      if (didSucceed) {
        emailEditor.commands.setContent(
          parseDraftHtml(buffer || emailEditor.getHTML()),
        );
        if (emailDraft?.uid) {
          void queryClient.invalidateQueries({
            queryKey: draftFeedbackQueryKey(emailDraft.uid),
          });
        }
      }
      if (onDone) onDone();
    };

    try {
      const resp = await fetch(url, {
        method: "GET",
        credentials: "include",
        signal: controller.signal,
        headers: {
          Accept: "text/event-stream",
        },
      });

      if (!resp.ok || !resp.body) {
        console.error("SSE regenerate HTTP error", resp.status);
        finishStream(receivedContent);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let pending = "";

      const processLine = (line: string) => {
        if (!line.startsWith("data:")) return;
        // SSE spec: if there is a single leading space after "data:", remove it.
        // Do NOT trim further; whitespace/newline-intent can be meaningful for spacing.
        let data = line.slice("data:".length);
        if (data.startsWith(" ")) data = data.slice(1);
        if (data.trim() === "[DONE]") {
          finishStream(receivedContent);
          return;
        }
        // Ignore the initial keep-alive `data:` flush (empty payload).
        if (data.length === 0) return;
        // Mark "receivedContent" only when we got non-whitespace content, but still append whitespace chunks.
        receivedContent = receivedContent || data.trim().length > 0;
        buffer += data;
        emailEditor.commands.setContent(parseDraftHtml(buffer));
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (finished) break;
        pending += decoder.decode(value, { stream: true });
        const lines = pending.split("\n");
        pending = lines.pop() ?? "";
        for (const raw of lines) {
          const line = raw.replace(/\r$/, "");
          if (line.length === 0) continue;
          processLine(line);
          if (finished) break;
        }
        if (finished) break;
      }

      finishStream(receivedContent);
    } catch (err) {
      console.error("SSE regenerate error", err);
      finishStream(receivedContent);
    }
  };

  const handleRegenerate = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }

    const prompt = emailDraftModificationPrompt.trim();
    if (!prompt) {
      console.warn("Please provide a modification prompt");
      return;
    }

    void startStream(prompt);
  };

  const handleQuickPrompt = (prompt: string, promptLabel: string) => {
    setLoadingPromptLabel(promptLabel);
    startStream(prompt, () => setLoadingPromptLabel(null));
  };

  const handleRemoveAttachment = (indexToRemove: number) => {
    const url = pdfUrls.get(`${indexToRemove}`);
    if (url) {
      URL.revokeObjectURL(url);
    }
    setAttachments(attachments.filter((_, index) => index !== indexToRemove));
  };

  const handleAddInvoices = async () => {
    if (invoiceIdsForAttach.length === 0) {
      toast.error("No customer matched for this thread.");
      return;
    }
    setIsLoadingInvoices(true);
    try {
      await addInvoicePdfsToAttachments(invoiceIdsForAttach);
    } catch (error) {
      console.error("Failed to add invoices", error);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const handleAddSelectedInvoices = async (externalIds: string[]) => {
    if (externalIds.length === 0) return;
    setIsAddingSelectedInvoices(true);
    try {
      await addInvoicePdfsToAttachments(externalIds);
    } catch (error) {
      console.error("Failed to add selected invoices", error);
    } finally {
      setIsAddingSelectedInvoices(false);
    }
  };

  const appendAttachmentFiles = (files: File[]) => {
    if (files.length === 0) return;

    setAttachments((prev) => {
      const existingKeys = new Set(
        prev.map((file) => `${file.name}-${file.size}`),
      );
      const newFiles = files.filter(
        (file) => !existingKeys.has(`${file.name}-${file.size}`),
      );
      return [...prev, ...newFiles];
    });
  };

  const handleLocalFilesSelected = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const selected = event.target.files;
    if (selected?.length) {
      appendAttachmentFiles(Array.from(selected));
    }
    event.target.value = "";
  };

  const openLocalFilePicker = () => {
    fileInputRef.current?.click();
  };

  const validateAndSetEmails = (
    values: string[],
    setEmails: React.Dispatch<React.SetStateAction<string[]>>,
    setError: React.Dispatch<React.SetStateAction<string | null>>,
    label: string,
  ) => {
    const normalized = normalizeEmails(values);
    const invalid = normalized.filter((email) => !EMAIL_REGEX.test(email));

    if (invalid.length > 0) {
      const validEmails = normalized.filter((email) => EMAIL_REGEX.test(email));
      setEmails(validEmails);
      setError(
        `Invalid ${label} ${invalid.length > 1 ? "addresses" : "address"}: ${invalid.join(", ")}`,
      );
      toast.error(
        `Invalid ${label} ${invalid.length > 1 ? "addresses" : "address"}: ${invalid.join(", ")}`,
      );
      return;
    }

    setError(null);
    setEmails(normalized);
  };

  const resolveLinkedPaymentIds = (): string[] | undefined => {
    if (invalidateReceivables && attachedInvoiceExternalIds.length > 0) {
      return attachedInvoiceExternalIds;
    }
    if (paymentExternalIds?.length) {
      return paymentExternalIds;
    }
    if (invoiceId) {
      return [invoiceId];
    }
    return undefined;
  };

  const wouldStatusChange = (status: string) =>
    status !== "In_progress" && status !== "Approved";

  const fetchPaymentStatus = async (
    externalId: string,
  ): Promise<string | null> => {
    try {
      const payment = await queryClient.fetchQuery({
        queryKey: paymentKeys.detail(externalId),
        queryFn: async () => {
          const { data } = await apiClient.get<Payment>(
            `/payments/${externalId}`,
          );
          return data;
        },
      });
      return payment.intention_status;
    } catch {
      return null;
    }
  };

  const validateSendInputs = (): {
    normalizedTo: string[];
    normalizedCc: string[];
    normalizedBcc: string[];
  } | null => {
    const normalizedTo = normalizeAndDedupeEmails(emailToAddress);
    if (normalizedTo.length === 0) {
      setEmailToError("Please add at least one recipient");
      toast.error("Please add at least one recipient");
      return null;
    }

    if (!subject.trim()) {
      toast.error("Subject is required before sending.");
      return null;
    }

    const normalizedCc = normalizeAndDedupeEmails(emailCcAddress);
    const normalizedBcc = normalizeAndDedupeEmails(emailBccAddress);

    setEmailToAddress(normalizedTo);
    setEmailCcAddress(normalizedCc);
    setEmailBccAddress(normalizedBcc);

    return { normalizedTo, normalizedCc, normalizedBcc };
  };

  const executeSend = async (updateIntentionStatus: boolean) => {
    if (blockSendIfAutoAttaching()) return;

    const validated = validateSendInputs();
    if (!validated) return;

    const { normalizedTo, normalizedCc, normalizedBcc } = validated;
    const emailData = {
      text: getEmailBodyHtml(),
      subject: subject,
      to: normalizedTo,
      cc: normalizedCc.length > 0 ? normalizedCc : null,
      bcc: normalizedBcc.length > 0 ? normalizedBcc : null,
      threadId: resolvedThreadId,
      replyMessageId: resolvedReplyMessageId,
    };
    const linkedPaymentIds = resolveLinkedPaymentIds();
    try {
      const response = await sendEmailMutation.mutateAsync({
        emailData,
        draftUrl: emailDraft?.uid || null,
        attachments,
        paymentExternalId: invoiceId ?? paymentExternalIds?.[0] ?? null,
        paymentExternalIds: linkedPaymentIds,
        invalidateReceivables: invalidateReceivables ?? false,
        updateIntentionStatus,
      });
      if (response.status === "success") {
        hasUserEditedSubject.current = false;
        hasUserEditedBody.current = false;
        setIntentionStatusModalOpen(false);
        toast.success("Email sent successfully!");
        onSendSuccess?.();
        if (shouldNavigateOnSuccess) {
          setTimeout(() => {
            navigate("/receivables");
          }, 1000);
        }
      }
    } catch (error) {
      console.error("Failed to send email", error, emailData);
      const apiError = (error as { response?: { data?: unknown } })?.response
        ?.data;
      const detail = (apiError as { detail?: unknown })?.detail;
      let message = "Failed to send email";
      if (Array.isArray(detail)) {
        message = detail
          .map((d) => (typeof d === "object" && d ? (d as any).msg : String(d)))
          .filter(Boolean)
          .join("; ");
      } else if (typeof detail === "string") {
        message = detail;
      } else if (detail && typeof detail === "object") {
        message = (detail as any).message || JSON.stringify(detail);
      } else if ((error as any)?.message) {
        message = (error as any).message;
      }
      toast.error(message);
      throw error;
    }
  };

  const requestSend = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (blockSendIfAutoAttaching()) return;

    const validated = validateSendInputs();
    if (!validated) return;

    const linkedPaymentIds = resolveLinkedPaymentIds();
    if (!linkedPaymentIds || linkedPaymentIds.length === 0) {
      await executeSend(true);
      return;
    }

    const invoiceStatuses: LinkedInvoiceStatus[] = [];
    let shouldShowModal = false;

    for (const externalId of linkedPaymentIds) {
      const status = await fetchPaymentStatus(externalId);
      if (status === null || wouldStatusChange(status)) {
        shouldShowModal = true;
      }
      invoiceStatuses.push({
        externalId,
        intentionStatus: status ?? "Unknown",
      });
    }

    if (shouldShowModal) {
      setLinkedInvoicesForModal(invoiceStatuses);
      setIntentionStatusModalOpen(true);
      return;
    }

    await executeSend(true);
  };

  const canAttachAllInvoices = invoiceIdsForAttach.length > 0;
  const canAttachSpecificInvoices = customerRep?.id != null;
  const isAttachingInvoices = isLoadingInvoices || isAddingSelectedInvoices;

  if (!canMutateOrg) {
    const subjectText =
      emailDraft?.subject ?? latestThreadSubject ?? initialSubject ?? "";
    const bodyHtml = initialBodyHtml || emailDraft?.text || "";
    return (
      <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          View only
        </p>
        {subjectText ? (
          <p className="text-sm font-medium text-slate-800">{subjectText}</p>
        ) : null}
        {bodyHtml ? (
          <div
            className="prose prose-sm max-w-none text-slate-700"
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
        ) : (
          <p className="text-sm text-slate-500">No draft to display.</p>
        )}
      </div>
    );
  }

  if (composerDisabled) {
    return (
      <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
        This invoice is excluded from templates and drafts. Turn on &quot;Send
        emails&quot; above to compose.
      </p>
    );
  }

  return (
    <div className="flex gap-4 w-full">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleLocalFilesSelected}
      />
      <div className="flex-1 flex flex-col">
        {invoiceId && (hasThread || emailDraft?.thread_id) ? (
          <p
            className="mb-3 w-fit rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-sm text-amber-900"
            aria-live="polite"
          >
            {threadModeLabel}
          </p>
        ) : null}
        <div className="flex flex-col mb-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-60">
              <div className="mb-1 flex h-5 items-center justify-between gap-2">
                <label
                  htmlFor="email-composer-to"
                  className="text-sm font-medium text-slate-700"
                >
                  To <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  className="shrink-0 cursor-pointer text-xs text-slate-600 underline"
                  onClick={() => setToggleCcBcc(!toggleCcBcc)}
                >
                  Cc, Bcc
                </button>
              </div>
              <TagsInput
                id="email-composer-to"
                required
                value={emailToAddress}
                error={emailToError}
                size="md"
                radius="md"
                styles={tagInputStyles}
                onChange={(values) =>
                  validateAndSetEmails(
                    values,
                    setEmailToAddress,
                    setEmailToError,
                    "To",
                  )
                }
                placeholder="Enter email addresses"
              />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="mb-1 flex h-5 items-center">
                <label
                  htmlFor="email-composer-subject"
                  className="text-sm font-medium text-slate-700"
                >
                  Subject
                </label>
              </div>
              <TextInput
                id="email-composer-subject"
                value={subject}
                size="md"
                radius="md"
                styles={composerInputStyles}
                onChange={(event) =>
                  handleSubjectChange(event.currentTarget.value)
                }
              />
            </div>
            <div className="flex shrink-0 items-center self-end">
              <Button
                variant="outline"
                radius="md"
                size="md"
                leftSection={<IconPaperclip size={16} />}
                onClick={openLocalFilePicker}
                disabled={isAttachingInvoices}
                style={
                  canAttachAllInvoices || canAttachSpecificInvoices
                    ? {
                        borderTopRightRadius: 0,
                        borderBottomRightRadius: 0,
                        borderRight:
                          "1px solid var(--mantine-color-default-border)",
                      }
                    : undefined
                }
              >
                Attach files
              </Button>
              {(canAttachAllInvoices || canAttachSpecificInvoices) && (
                <Menu
                  position="bottom-end"
                  withinPortal
                  transitionProps={{ transition: "pop", duration: 200 }}
                  shadow="md"
                  radius="md"
                >
                  <Menu.Target>
                    <Button
                      variant="outline"
                      radius="md"
                      size="md"
                      disabled={isAttachingInvoices}
                      loading={isAttachingInvoices}
                      aria-label="Attach invoice options"
                      style={{
                        borderTopLeftRadius: 0,
                        borderBottomLeftRadius: 0,
                        paddingLeft: 6,
                        paddingRight: 8,
                      }}
                    >
                      <IconChevronDown size={16} />
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {canAttachAllInvoices && (
                      <Menu.Item
                        leftSection={<IconFileInvoice size={16} />}
                        onClick={() => void handleAddInvoices()}
                        disabled={isAttachingInvoices}
                      >
                        Attach all invoices
                      </Menu.Item>
                    )}
                    {canAttachSpecificInvoices && (
                      <Menu.Item
                        leftSection={<IconFileInvoice size={16} />}
                        onClick={() => setChooseInvoicesModalOpen(true)}
                        disabled={isAttachingInvoices}
                      >
                        Attach specific invoices
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              )}
            </div>
          </div>
          {toggleCcBcc ? (
            <div className="mt-2 flex flex-wrap gap-3 items-end">
              <div className="min-w-60 flex-1">
                <TagsInput
                  styles={{
                    ...tagInputStyles,
                    input: { ...tagInputStyles.input, color: "gray" },
                  }}
                  value={emailCcAddress}
                  description="Cc"
                  error={emailCcError}
                  size="md"
                  radius="md"
                  onChange={(values) =>
                    validateAndSetEmails(
                      values,
                      setEmailCcAddress,
                      setEmailCcError,
                      "Cc",
                    )
                  }
                  placeholder="Enter email addresses"
                />
              </div>
              <div className="min-w-60 flex-1">
                <TagsInput
                  styles={{
                    ...tagInputStyles,
                    input: { ...tagInputStyles.input, color: "gray" },
                  }}
                  value={emailBccAddress}
                  description="Bcc"
                  error={emailBccError}
                  size="md"
                  radius="md"
                  onChange={(values) =>
                    validateAndSetEmails(
                      values,
                      setEmailBccAddress,
                      setEmailBccError,
                      "Bcc",
                    )
                  }
                  placeholder="Enter email addresses"
                />
              </div>
            </div>
          ) : null}
        </div>
        <div
          className={[
            "w-full min-w-0 overflow-x-hidden",
            isStreaming ? "streaming-loading" : "",
          ].join(" ")}
        >
          <EmailEditor
            editor={emailEditor}
            language={effectiveLanguage}
            minHeight={editorMinHeight}
          />
        </div>
        {quotedReplyTailHtml ? (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
              Quoted message
            </p>
            <div
              className="max-h-40 overflow-y-auto text-sm text-slate-600"
              dangerouslySetInnerHTML={{ __html: quotedReplyTailHtml }}
            />
          </div>
        ) : null}
        {signature?.enabled && (
          <div className="mt-2 p-2 bg-gray-50 rounded border border-gray-200">
            <Text size="xs" c="dimmed" mb="xs">
              Your email signature will be automatically appended:
            </Text>
            <div className="text-sm flex items-center gap-3">
              {signature.image_base64 && (
                <img
                  src={signature.image_base64}
                  alt="Signature"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "contain",
                  }}
                />
              )}
              {signature.text && (
                <div dangerouslySetInnerHTML={{ __html: signature.text }} />
              )}
            </div>
          </div>
        )}
        <AttachmentsPreviewPanel
          attachments={attachments}
          pdfUrls={pdfUrls}
          onRemoveAttachment={handleRemoveAttachment}
        />
        {showAiAssist ? (
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex flex-col gap-2 lg:flex-row lg:gap-4">
              <div className="flex min-w-0 flex-1 flex-col gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  AI assist
                </p>
                <EmailPromptBubbles
                  prompts={quickPrompts}
                  onPromptClick={(prompt: string, promptLabel: string) =>
                    handleQuickPrompt(prompt, promptLabel)
                  }
                  loadingPromptLabel={loadingPromptLabel}
                />
              </div>
              {invoiceId ? (
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 lg:w-[min(100%,22rem)] lg:shrink-0 xl:w-[min(100%,26rem)]">
                  Template
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-4">
              <TextInput
                className="min-w-0 flex-1"
                value={emailDraftModificationPrompt}
                placeholder="Use AI to create or change the email..."
                rightSection={
                  <ActionIcon
                    variant="default"
                    aria-label="Regenerate"
                    radius="md"
                    c="primary.6"
                    size={"l"}
                    onClick={handleRegenerate}
                    disabled={
                      isRegenerating || !emailDraftModificationPrompt.trim()
                    }
                  >
                    <IconRefresh
                      className={
                        isRegenerating ? "animate-spin duration-200" : ""
                      }
                    />
                  </ActionIcon>
                }
                onChange={(event) =>
                  setEmailDraftModificationPrompt(event.currentTarget.value)
                }
              />
              {invoiceId ? (
                <InvoiceTemplateDraftGenerator
                  invoiceId={invoiceId}
                  hasExistingDraft={Boolean(emailDraft?.uid)}
                  disabled={composerDisabled}
                  variant="compact"
                  hideLabel
                  className="w-full lg:w-[min(100%,22rem)] lg:shrink-0 xl:w-[min(100%,26rem)]"
                />
              ) : null}
            </div>
          </div>
        ) : null}
        <div className="flex flex-row mt-4 gap-2 items-center">
          {isSentEmailLoading ? (
            <Loader size={20} />
          ) : (
            <div className="flex items-center">
              {invoiceId ? (
                <div className="flex items-center">
                  <Button
                    variant="filled"
                    radius="md"
                    pl="10"
                    size={"l"}
                    leftSection={<IconSend />}
                    disabled={isAutoAttachingInvoicePdfs}
                    onClick={() => void requestSend()}
                    style={{
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                      borderRight: "1px solid rgba(255, 255, 255, 0.3)",
                    }}
                  >
                    Send
                  </Button>
                  <Menu
                    position="bottom-end"
                    withinPortal
                    transitionProps={{ transition: "pop", duration: 200 }}
                    shadow="lg"
                    radius="md"
                    styles={{
                      dropdown: {
                        padding: "4px 0",
                      },
                      item: {
                        padding: "8px 12px",
                        borderRadius: 6,
                      },
                    }}
                  >
                    <Menu.Target>
                      <Button
                        variant="filled"
                        radius="md"
                        size={"l"}
                        disabled={isAutoAttachingInvoicePdfs}
                        style={{
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          paddingLeft: "8px",
                          paddingRight: "10px",
                        }}
                      >
                        <IconChevronDown size={16} />
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      {hasThread ? (
                        <Menu.Item
                          leftSection={<IconMail size={16} />}
                          disabled={isAutoAttachingInvoicePdfs}
                          onClick={() => {
                            setInitialModalView("pickThread");
                            setSelectedAction(null);
                            setActionModalOpen(true);
                          }}
                        >
                          Reply to a thread
                        </Menu.Item>
                      ) : null}
                      <Menu.Item
                        leftSection={<IconMailForward size={16} />}
                        disabled={isAutoAttachingInvoicePdfs}
                        onClick={() => {
                          setSendAsNewEmail(true);
                          void requestSend();
                        }}
                      >
                        Send as new email
                      </Menu.Item>
                      {customerRep ? (
                        <Menu.Item
                          leftSection={<IconFileText size={16} />}
                          disabled={isAutoAttachingInvoicePdfs}
                          onClick={() => {
                            setInitialModalView("updateDescription");
                            setSelectedAction("updateDescription");
                            setActionModalOpen(true);
                          }}
                        >
                          Update Customer Description
                        </Menu.Item>
                      ) : null}
                    </Menu.Dropdown>
                  </Menu>
                  <Button
                    variant="filled"
                    radius="md"
                    color="green"
                    pl="10"
                    ml={10}
                    size={"l"}
                    leftSection={<IconCalendarClock />}
                    onClick={() => setFollowUpModalOpen(true)}
                  >
                    Follow Up
                  </Button>
                </div>
              ) : (
                <Button
                  variant="filled"
                  radius="md"
                  pl="10"
                  size={"l"}
                  leftSection={<IconSend />}
                  disabled={isAutoAttachingInvoicePdfs}
                  onClick={() => void requestSend()}
                >
                  Send
                </Button>
              )}
            </div>
          )}
          {invoiceId && autoSaveStatus === "saving" && (
            <Text size="sm" c="dimmed" className="ml-2">
              Saving draft...
            </Text>
          )}
          {invoiceId && autoSaveStatus === "saved" && (
            <Text size="sm" c="dimmed" className="ml-2">
              Draft saved
            </Text>
          )}
        </div>
        {invoiceId && (
          <FollowUpModal
            externalId={invoiceId}
            opened={followUpModalOpen}
            onClose={() => setFollowUpModalOpen(false)}
          />
        )}
        {invoiceId && (hasThread || customerRep) ? (
          <SendActionModal
            opened={actionModalOpen}
            action={selectedAction}
            onClose={() => {
              setActionModalOpen(false);
              setSelectedAction(null);
              setInitialModalView(null);
            }}
            invoiceId={invoiceId}
            customerRepId={customerRep?.id}
            currentLanguage={customerRep?.language}
            onSend={handleActionAndSend}
            initialView={initialModalView}
            threads={threadOptions}
            onSendAsReplyToThread={async (threadId) => {
              setSelectedReplyThreadId(threadId);
              setSendAsNewEmail(false);
              await requestSend();
            }}
          />
        ) : null}
        <ChooseInvoicesModal
          opened={chooseInvoicesModalOpen}
          onClose={() => setChooseInvoicesModalOpen(false)}
          customerRepId={customerRep?.id}
          onAddInvoices={handleAddSelectedInvoices}
          isLoading={isAddingSelectedInvoices}
          currentInvoiceId={invoiceId}
        />
        <SendEmailIntentionStatusModal
          opened={intentionStatusModalOpen}
          onClose={() => setIntentionStatusModalOpen(false)}
          linkedInvoices={linkedInvoicesForModal}
          isSending={isSentEmailLoading}
          onMarkInProgressAndSend={() => executeSend(true)}
          onSendWithoutStatusChange={() => executeSend(false)}
        />
      </div>
    </div>
  );
};
